import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  type ConfirmationResult,
  RecaptchaVerifier,
  signInWithPhoneNumber,
  signOut,
} from "firebase/auth";
import { firebaseAuth } from "../config/firebase";

type RoleValue = "supplier" | "customer" | "admin";

type SignupPayload = {
  email: string;
  username: string;
  phone: string;
  password: string;
  confirmPassword: string;
  role: RoleValue;
};

const MAX_CODE_ATTEMPTS = 3;
const RECAPTCHA_CONTAINER_ID = "signup-phone-recaptcha";

export function useSignupForm(params: {
  backendUrl?: string;
  translate: (k: string) => string;
  onSuccess: () => void;
}) {
  const { backendUrl, translate, onSuccess } = params;

  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [phone, setPhone] = useState("");
  const [role, setRole] = useState<RoleValue>("customer");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreePolicy, setAgreePolicy] = useState(false);
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationPhone, setVerificationPhone] = useState("");
  const [verificationMessage, setVerificationMessage] = useState("");
  const [attemptsLeft, setAttemptsLeft] = useState(MAX_CODE_ATTEMPTS);
  const [isVerificationStep, setIsVerificationStep] = useState(false);

  const signupButtonRef = useRef<HTMLButtonElement>(null);
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);
  const recaptchaWidgetIdRef = useRef<number | null>(null);
  const confirmationResultRef = useRef<ConfirmationResult | null>(null);
  const pendingSignupRef = useRef<SignupPayload | null>(null);

  useEffect(() => {
    return () => {
      clearRecaptchaVerifier();
      void signOut(firebaseAuth).catch(() => undefined);
    };
  }, []);

  const isValidEmail = (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

  const normalizePhone = (value: string) => {
    let normalized = value.trim().replace(/[^\d+]/g, "");

    if (normalized.startsWith("00")) {
      normalized = `+${normalized.slice(2)}`;
    }

    if (!/^\+[1-9]\d{7,14}$/.test(normalized)) {
      return "";
    }

    return normalized;
  };

  const getFirebaseRequestErrorMessage = (error: any) => {
    switch (error?.code) {
      case "auth/invalid-phone-number":
        return translate("Use phone format like +213XXXXXXXXX");
      case "auth/too-many-requests":
        return translate("Too many verification requests. Please try again later.");
      case "auth/captcha-check-failed":
        return translate("Phone verification failed. Please try again.");
      default:
        return (
          error?.message ||
          translate("Phone verification failed. Please try again.") ||
          "Phone verification failed. Please try again."
        );
    }
  };

  const getFirebaseCodeErrorMessage = (attemptsRemaining: number) => {
    if (attemptsRemaining <= 0) {
      return (
        translate("You have used all 3 verification attempts. Please request a new code.") ||
        "You have used all 3 verification attempts. Please request a new code."
      );
    }

    return `${
      translate("The verification code is incorrect.") || "The verification code is incorrect."
    } ${translate("Attempts remaining") || "Attempts remaining"}: ${attemptsRemaining}.`;
  };

  const clearRecaptchaVerifier = () => {
    if (!recaptchaVerifierRef.current) {
      return;
    }

    try {
      recaptchaVerifierRef.current.clear();
    } catch (error) {
      console.error("reCAPTCHA clear error", error);
    } finally {
      recaptchaVerifierRef.current = null;
      recaptchaWidgetIdRef.current = null;
      const container = document.getElementById(RECAPTCHA_CONTAINER_ID);
      if (container) {
        container.innerHTML = "";
      }
    }
  };

  const resetRecaptchaWidget = () => {
    if (recaptchaWidgetIdRef.current === null) {
      return;
    }

    const grecaptcha = (
      window as Window & {
        grecaptcha?: { reset: (widgetId?: number) => void };
      }
    ).grecaptcha;

    try {
      grecaptcha?.reset(recaptchaWidgetIdRef.current);
    } catch (error) {
      console.error("reCAPTCHA reset error", error);
    }
  };

  const resetVerificationState = (clearVerifier = false) => {
    confirmationResultRef.current = null;
    pendingSignupRef.current = null;
    setVerificationCode("");
    setVerificationPhone("");
    setVerificationMessage("");
    setAttemptsLeft(MAX_CODE_ATTEMPTS);
    setIsVerificationStep(false);

    if (clearVerifier) {
      clearRecaptchaVerifier();
    } else {
      resetRecaptchaWidget();
    }
  };

  const validate = () => {
    setAlertMessage("");

    const trimmedEmail = email.trim();
    const trimmedUsername = username.trim() || trimmedEmail;
    const normalizedPhone = normalizePhone(phone);

    if (!trimmedEmail) return translate("Email is required") || "Email is required";
    if (!isValidEmail(trimmedEmail)) return translate("Email is invalid") || "Email is invalid";
    if (!trimmedUsername) return translate("Username is required") || "Username is required";
    if (!normalizedPhone) {
      return translate("Use phone format like +213XXXXXXXXX") || "Use phone format like +213XXXXXXXXX";
    }
    if (!password) return translate("Password is required") || "Password is required";
    if (password.length < 6) return translate("Password is too short") || "Password is too short";
    if (confirmPassword !== password) {
      return translate("Passwords do not match") || "Passwords do not match";
    }
    if (!agreePolicy) {
      return translate("You must accept privacy policy") || "You must accept privacy policy";
    }

    return "";
  };

  const buildSignupPayload = (): SignupPayload | null => {
    const normalizedPhone = normalizePhone(phone);

    if (!normalizedPhone) {
      setAlertMessage(
        translate("Use phone format like +213XXXXXXXXX") || "Use phone format like +213XXXXXXXXX"
      );
      return null;
    }

    return {
      email: email.trim(),
      username: username.trim() || email.trim(),
      phone: normalizedPhone,
      password,
      confirmPassword,
      role,
    };
  };

  const ensureRecaptchaVerifier = async () => {
    const container = document.getElementById(RECAPTCHA_CONTAINER_ID);

    if (!container) {
      throw new Error(
        translate("Phone verification is not ready. Please refresh and try again.") ||
          "Phone verification is not ready. Please refresh and try again."
      );
    }

    if (!recaptchaVerifierRef.current) {
      const verifier = new RecaptchaVerifier(firebaseAuth, RECAPTCHA_CONTAINER_ID, {
        size: "invisible",
      });

      recaptchaVerifierRef.current = verifier;
      recaptchaWidgetIdRef.current = await verifier.render();
      return verifier;
    }

    if (recaptchaWidgetIdRef.current === null) {
      recaptchaWidgetIdRef.current = await recaptchaVerifierRef.current.render();
    } else {
      resetRecaptchaWidget();
    }

    return recaptchaVerifierRef.current;
  };

  const requestVerificationCode = async () => {
    const errorMessage = validate();
    if (errorMessage) {
      setAlertMessage(errorMessage);
      return;
    }

    const signupPayload = buildSignupPayload();
    if (!signupPayload) {
      return;
    }

    try {
      setLoading(true);
      setAlertMessage("");
      setVerificationMessage("");

      await signOut(firebaseAuth).catch(() => undefined);
      resetVerificationState();

      const verifier = await ensureRecaptchaVerifier();
      const confirmationResult = await signInWithPhoneNumber(
        firebaseAuth,
        signupPayload.phone,
        verifier
      );

      confirmationResultRef.current = confirmationResult;
      pendingSignupRef.current = signupPayload;
      setVerificationPhone(signupPayload.phone);
      setVerificationCode("");
      setAttemptsLeft(MAX_CODE_ATTEMPTS);
      setVerificationMessage(
        `${translate("A verification code has been sent to") || "A verification code has been sent to"} ${
          signupPayload.phone
        }.`
      );
      setIsVerificationStep(true);
    } catch (error: any) {
      resetVerificationState();
      setAlertMessage(getFirebaseRequestErrorMessage(error));
      console.error("Phone verification request error", error);
    } finally {
      setLoading(false);
    }
  };

  const verifyCodeAndCreateAccount = async () => {
    const code = verificationCode.trim();
    const confirmationResult = confirmationResultRef.current;
    const signupPayload = pendingSignupRef.current;

    if (!confirmationResult || !signupPayload) {
      setAlertMessage(
        translate("Send a verification code first.") || "Send a verification code first."
      );
      return;
    }

    if (!code) {
      setAlertMessage(
        translate("Verification code is required") || "Verification code is required"
      );
      return;
    }

    if (attemptsLeft <= 0) {
      setAlertMessage(getFirebaseCodeErrorMessage(0));
      return;
    }

    try {
      setLoading(true);
      setAlertMessage("");

      const userCredential = await confirmationResult.confirm(code);
      const firebaseIdToken = await userCredential.user.getIdToken();

      try {
        await axios.post(
          `${backendUrl}/api/signup`,
          {
            ...signupPayload,
            firebaseIdToken,
          },
          { withCredentials: true }
        );
      } catch (error: any) {
        await signOut(firebaseAuth).catch(() => undefined);
        resetVerificationState();

        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Signup error.";

        setAlertMessage(message);
        console.error("Signup error", error);
        return;
      }

      await signOut(firebaseAuth).catch(() => undefined);
      resetVerificationState();
      onSuccess();
    } catch (error: any) {
      const nextAttempts = Math.max(attemptsLeft - 1, 0);

      await signOut(firebaseAuth).catch(() => undefined);

      if (nextAttempts === 0) {
        resetVerificationState();
      } else {
        setAttemptsLeft(nextAttempts);
        setVerificationCode("");
      }

      setAlertMessage(getFirebaseCodeErrorMessage(nextAttempts));
      console.error("Phone verification confirm error", error);
    } finally {
      setLoading(false);
    }
  };

  const resendVerificationCode = () => {
    void requestVerificationCode();
  };

  const cancelVerification = () => {
    setAlertMessage("");
    resetVerificationState();
    void signOut(firebaseAuth).catch(() => undefined);
  };

  const submit = () => {
    if (isVerificationStep) {
      void verifyCodeAndCreateAccount();
      return;
    }

    void requestVerificationCode();
  };

  return {
    email,
    setEmail,
    username,
    setUsername,
    phone,
    setPhone,
    role,
    setRole,
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    agreePolicy,
    setAgreePolicy,
    loading,
    alertMessage,
    setAlertMessage,
    signupButtonRef,
    submit,
    verificationCode,
    setVerificationCode,
    verificationPhone,
    verificationMessage,
    attemptsLeft,
    isVerificationStep,
    resendVerificationCode,
    cancelVerification,
    isFormLocked: loading || isVerificationStep,
  };
}
